import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  GameEvent,
  GameplayType,
  GameplayLogRequest,
} from "@nestcoinco/hyper-api-gateway-api-client";

import { QUERY_KEYS } from "../constants";
import {
  handleLogGameplayEvent,
  LogGameplayEventParams,
} from "../../api-clients/gameplayLogsClient";
import { GamePlayEvents, GameType } from "../../utils/constants";
import Logger from "../../utils/logger";

/**
 * maps internal GamePlayEvents (from iframe postMessage) to the SDK GameEvent enum.
 */
const GAME_PLAY_EVENT_TO_API_EVENT: Partial<Record<GamePlayEvents, GameEvent>> =
  {
    [GamePlayEvents.GAME_START]: GameEvent.GAME_START,
    [GamePlayEvents.GAME_END]: GameEvent.GAME_END,
    [GamePlayEvents.PRACTICE_GAME_END]: GameEvent.GAME_END,
    [GamePlayEvents.GAME_FAILED]: GameEvent.GAME_FAILED,
    [GamePlayEvents.GAME_PAUSE]: GameEvent.GAME_PAUSE,
    [GamePlayEvents.GAME_RESUME]: GameEvent.GAME_RESUME,
    [GamePlayEvents.SCORE_RECEIVED]: GameEvent.SCORE_RECEIVED,
  };

/**
 * maps internal GameType to the SDK GameplayType enum.
 */
const GAME_TYPE_TO_GAMEPLAY_TYPE: Record<string, GameplayType> = {
  [GameType.PRACTICE]: GameplayType.PRACTICE,
  [GameType.SOLO]: GameplayType.PRACTICE,
  [GameType.BATTLE]: GameplayType.TOURNAMENT,
  [GameType.TOURNAMENT]: GameplayType.TOURNAMENT,
  [GameType.CHALLENGE]: GameplayType.TOURNAMENT,
};

interface UseLogGameplayEventOptions {
  entryId?: string;
  gameId?: string;
  gameType: GameType | null;
}

function useLogGameplayEventMutation({
  entryId,
  gameId,
  gameType,
}: UseLogGameplayEventOptions) {
  const loggedEventsRef = useRef<Set<string>>(new Set());

  const mutation = useMutation({
    mutationKey: [QUERY_KEYS.GAME.LOG_GAMEPLAY_EVENT],
    mutationFn: (params: LogGameplayEventParams) =>
      handleLogGameplayEvent(params),
    onError: (error) => {
      Logger.error(`Failed to log gameplay event: ${error}`);
    },
  });

  const logGameplayEvent = useCallback(
    (event: GamePlayEvents, eventData?: { score?: number; time?: number }) => {
      if (!entryId || !gameId || !gameType) return;

      const apiEvent = GAME_PLAY_EVENT_TO_API_EVENT[event];
      if (!apiEvent) return;

      const playType = GAME_TYPE_TO_GAMEPLAY_TYPE[gameType];
      if (!playType) return;

      if (apiEvent === GameEvent.SCORE_RECEIVED && eventData?.score == null) {
        return;
      }

      const eventKey = `${entryId}:${apiEvent}`;
      const isTerminalEvent =
        apiEvent === GameEvent.GAME_END || apiEvent === GameEvent.GAME_FAILED;

      if (isTerminalEvent && loggedEventsRef.current.has(eventKey)) return;
      if (isTerminalEvent) loggedEventsRef.current.add(eventKey);

      const gameplayLogRequest: GameplayLogRequest = {
        gameId,
        event: apiEvent,
        type: playType,
        ...(eventData?.score != null && { score: eventData.score }),
        ...(eventData?.time != null && { time: eventData.time }),
      };

      mutation.mutate({ entryId, gameplayLogRequest });
    },
    [entryId, gameId, gameType, mutation]
  );

  return { mutation, logGameplayEvent };
}

export default useLogGameplayEventMutation;
